import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import BondsRepository from '@/finsight/infrastructure/repository/BondsRepository';
import ErrorCodeHelper from '@finsight/error-codes';
import { logout } from '@/finsight/application/actions/header/actions';
import { useDIContext } from '@/Framework/DI/DIContext';
import filtersSelectors from '@/finsight/application/actions/bonds/filters/selectors';
import { useFiltersContext } from '@/finsight/application/FiltersContext';
import { useBondsAssetClassesContext } from '@/finsight/application/Bonds/BondsAssetClassesContext';
import usePrevious from '@/Framework/hooks/usePrevious';
import { IInventoryItem, ISubscriptionStatus } from '@/finsight/domain/vo/Bond/IBonds';
import { IDatetime } from '@/Framework/dateHelpers/Datetime';
import { initialInventoriesData } from './constants';
import useGetBondsPayload from '@/finsight/application/Bonds/useGetBondsPayload';

interface IInventories {
  collection: IInventoryItem[],
  totalCount: number,
}

export interface IInventoryData {
  inventories?: IInventories,
  lastImportAt?: IDatetime,
  subscriptionStatus?: ISubscriptionStatus,
}

const stateSelector = (state) => ({
  filter: filtersSelectors.getFiltersPayload(state),
});

export const useInventoryTable = (initial: IInventoryData, isTile?: boolean) => {
  const [inventoriesData, setInventoriesData] = useState<IInventoryData>(initial);
  const [isInventoriesFetching, setIsInventoriesFetching] = useState(false);
  const { container } = useDIContext();
  const dispatch = useDispatch();
  const { filter } = useSelector(stateSelector);
  const { currentAssetClass } = useBondsAssetClassesContext();
  const { params } = useFiltersContext();
  const prevParams = usePrevious(params) || params;

  const { getPayload } = useGetBondsPayload();

  useEffect(() => setInventoriesData(initial), [initial]);

  useEffect(() => {
    const shouldDenyFetch = (!currentAssetClass || !isTile || !filter) && isEqual(prevParams, params);
    if (shouldDenyFetch) return;
    getInventories(getPayload({}));
  }, [
    params.perPage,
    params.page,
    params.sortBy,
    params.sortOrder,
    currentAssetClass,
    filter,
  ]);

  const getInventories = async (payload) => {
    setIsInventoriesFetching(true);

    try {
      const bondsRepository = container.get(BondsRepository);
      const response = await bondsRepository.getInventoryList(payload);
      setInventoriesData(response);
    } catch (e) {
      if (Dispatcher.isAbortError(e)) {
        return;
      }
      if (e.error?.code === ErrorCodeHelper.getCodeByName('FINSIGHT_BOND_INVENTORY_NOT_AUTHENTICATED_EXCEPTION')) {
        setInventoriesData({
          ...e.error,
          ...initialInventoriesData,
          inventories: { ...e.error.data },
        });
        dispatch(logout());
      }
    } finally {
      setIsInventoriesFetching(false);
    }
  };

  const debouncedGetInventories = useCallback(debounce(getInventories, 500), []);

  return {
    isInventoriesFetching,
    getInventories,
    setInventoriesData,
    inventoriesData,
    debouncedGetInventories,
  };
};

type InventoryTableContextType = ReturnType<typeof useInventoryTable>;

export const InventoryTableContext = createContext<InventoryTableContextType>(null);

export const useInventoryTableContext = () => {
  const context = useContext(InventoryTableContext);
  if (!context) {
    throw new Error('useInventoryTableContext must be used within the InventoryTableContext');
  }
  return context;
};

interface IProps {
  initial: IInventoryData,
  isTile?: boolean,
  children: React.ReactNode,
}

const InventoryTableContextProvider = ({ initial, isTile, children }: IProps) => (
  <InventoryTableContext.Provider value={ useInventoryTable(initial, isTile) }>
    { children }
  </InventoryTableContext.Provider>
);

export default InventoryTableContextProvider;
